import React from 'react';
import {clampReviewScore, getFilledStarCount, STORE_PRODUCT_STAR_COUNT} from './filled-star-count';

const STAR_SLOT_INDEXES = Array.from({length: STORE_PRODUCT_STAR_COUNT}, (_, starIndex) => starIndex);

export type StoreProductRatingVisibility = {
    reviewCount: number;
    isMyFrames: boolean;
    portalId: number | null | undefined;
    allowReviews: boolean;
};

export type StoreProductRatingProps = {
    productId: number;
    reviewScore: number;
    reviewCount: number;
};

/** Same suppress rules as the previous inline store-card condition. */
export function shouldShowStoreProductRating({reviewCount, isMyFrames, portalId, allowReviews}: StoreProductRatingVisibility): boolean {
    return reviewCount > 0 && !isMyFrames && portalId !== 3 && allowReviews;
}

export function getStoreProductRatingDescriptionId(productId: number): string {
    return `store-product-rating-${productId}`;
}

export function getStoreProductRatingAriaDescribedBy(productId: number, visibility: StoreProductRatingVisibility): string | undefined {
    return shouldShowStoreProductRating(visibility) ? getStoreProductRatingDescriptionId(productId) : undefined;
}

function formatRatingScore(score: number): string {
    if (Number.isInteger(score)) {
        return String(score);
    }

    return String(Number(score.toFixed(2)));
}

/** e.g. "4.1 out of 5 stars, 12 reviews." */
export function formatStoreProductRatingDescription(reviewScore: number, reviewCount: number): string {
    const scoreText = formatRatingScore(clampReviewScore(reviewScore));
    const count = Number(reviewCount);
    const safeCount = Number.isFinite(count) && count > 0 ? count : 0;
    const reviewWord = safeCount === 1 ? 'review' : 'reviews';

    return `${scoreText} out of 5 stars, ${safeCount} ${reviewWord}.`;
}

export const StoreProductRating: React.FC<StoreProductRatingProps> = ({productId, reviewScore, reviewCount}) => {
    const descriptionId = getStoreProductRatingDescriptionId(productId);
    const filledStars = getFilledStarCount(reviewScore);
    const description = formatStoreProductRatingDescription(reviewScore, reviewCount);

    return (
        <>
            <span
                id={descriptionId}
                className="sr-only"
            >
                {description}
            </span>
            <div className="rating">
                <div className="rating-inner">
                    <div className="stars">
                        {STAR_SLOT_INDEXES.map(starIndex => (
                            <span key={starIndex}>
                                <i
                                    className={`fa fa-star ${starIndex < filledStars ? ' checked' : ''}`}
                                    role="presentation"
                                ></i>
                            </span>
                        ))}
                    </div>
                    <span className="rating-desc">({reviewCount.toString()})</span>
                </div>
            </div>
        </>
    );
};
